import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  SlidersHorizontal,
  X,
  AlertTriangle,
  TrendingUp,
  Home,
  DollarSign,
  Loader2,
  Search,
} from "lucide-react";
import { propertyTypeLabels } from "@/data/mockProperties";
import { AddressAutocomplete } from "./AddressAutocomplete";
import { AddressSuggestion } from "@/hooks/use-address-autocomplete";

export interface PropertyFilters {
  search: string;
  priceMin?: number;
  priceMax?: number;
  bedsMin?: number;
  bathsMin?: number;
  sqftMin?: number;
  sqftMax?: number;
  propertyTypes: string[];
  // Distress filters
  foreclosure: boolean;
  preForeclosure: boolean;
  auction: boolean;
  reo: boolean;
  // Financial filters
  capRateMin?: number;
  cashOnCashMin?: number;
  grmMax?: number;
  // Deal indicator filters
  priceReduced: boolean;
  belowMarketValue: boolean;
  motivatedSeller: boolean;
  daysOnMarketMax?: number;
}

export type SortOption =
  | "newest"
  | "price_low"
  | "price_high"
  | "cap_rate"
  | "coc"
  | "days_high"
  | "days_low";

interface PropertyFiltersProps {
  filters: PropertyFilters;
  onFiltersChange: (filters: PropertyFilters) => void;
  onSuggestionSelect?: (suggestion: AddressSuggestion) => void;
  /** Fires when the user applies filters (same as main Search) — e.g. live MLS search. */
  onSearch?: () => void;
  searchLoading?: boolean;
  resultCount: number;
  sortBy?: SortOption;
  onSortChange?: (sort: SortOption) => void;
}

export function PropertyFiltersBar({
  filters,
  onFiltersChange,
  onSuggestionSelect,
  onSearch,
  searchLoading = false,
  resultCount,
  sortBy = "newest",
  onSortChange,
}: PropertyFiltersProps) {
  const [isOpen, setIsOpen] = useState(false);

  const activeFilterCount = [
    filters.priceMin || filters.priceMax,
    filters.bedsMin,
    filters.bathsMin,
    filters.propertyTypes.length > 0,
    filters.foreclosure ||
      filters.preForeclosure ||
      filters.auction ||
      filters.reo,
    filters.capRateMin || filters.cashOnCashMin,
    filters.priceReduced || filters.belowMarketValue || filters.motivatedSeller,
    filters.daysOnMarketMax,
  ].filter(Boolean).length;

  const clearFilters = () => {
    onFiltersChange({
      search: filters.search,
      propertyTypes: [],
      foreclosure: false,
      preForeclosure: false,
      auction: false,
      reo: false,
      priceReduced: false,
      belowMarketValue: false,
      motivatedSeller: false,
    });
  };

  return (
    <div className="flex flex-col gap-4 p-4 bg-card border-b border-border">
      {/* Search Bar */}
      <div className="flex gap-2">
        {/* Address Autocomplete Search */}
        <AddressAutocomplete
          value={filters.search}
          onChange={(value) => onFiltersChange({ ...filters, search: value })}
          onSelect={onSuggestionSelect}
          placeholder="Search by address, city, or zip..."
          className="flex-1"
        />

        {/* Inline Search Properties primary button (mirrors find-properties.html). */}
        {onSearch && (
          <Button
            onClick={onSearch}
            disabled={searchLoading}
            className="gap-2"
          >
            {searchLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Search className="w-4 h-4" />
            )}
            Search Properties
          </Button>
        )}

        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" className="gap-2">
              <SlidersHorizontal className="w-4 h-4" />
              Filters
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent className="w-full sm:max-w-md overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="flex items-center justify-between">
                <span>Filters</span>
                {activeFilterCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    Clear all
                  </Button>
                )}
              </SheetTitle>
            </SheetHeader>

            <Accordion
              type="multiple"
              defaultValue={["price", "distress", "financial", "deal"]}
              className="mt-6"
            >
              {/* Price & Basics */}
              <AccordionItem value="price">
                <AccordionTrigger className="text-sm font-medium">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Price & Basics
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-4 pt-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Min Price</Label>
                      <Input
                        type="number"
                        placeholder="$0"
                        value={filters.priceMin || ""}
                        onChange={(e) =>
                          onFiltersChange({
                            ...filters,
                            priceMin: e.target.value
                              ? parseInt(e.target.value)
                              : undefined,
                          })
                        }
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Max Price</Label>
                      <Input
                        type="number"
                        placeholder="No max"
                        value={filters.priceMax || ""}
                        onChange={(e) =>
                          onFiltersChange({
                            ...filters,
                            priceMax: e.target.value
                              ? parseInt(e.target.value)
                              : undefined,
                          })
                        }
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Min Beds</Label>
                      <Select
                        value={filters.bedsMin?.toString() || "any"}
                        onValueChange={(v) =>
                          onFiltersChange({
                            ...filters,
                            bedsMin: v && v !== "any" ? parseInt(v) : undefined,
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Any" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Any</SelectItem>
                          {[1, 2, 3, 4, 5].map((n) => (
                            <SelectItem key={n} value={n.toString()}>
                              {n}+
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Min Baths</Label>
                      <Select
                        value={filters.bathsMin?.toString() || "any"}
                        onValueChange={(v) =>
                          onFiltersChange({
                            ...filters,
                            bathsMin:
                              v && v !== "any" ? parseFloat(v) : undefined,
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Any" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Any</SelectItem>
                          {[1, 1.5, 2, 2.5, 3, 4].map((n) => (
                            <SelectItem key={n} value={n.toString()}>
                              {n}+
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs mb-2 block">Property Type</Label>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(propertyTypeLabels).map(
                        ([value, label]) => (
                          <Badge
                            key={value}
                            variant={
                              filters.propertyTypes.includes(value)
                                ? "default"
                                : "outline"
                            }
                            className="cursor-pointer"
                            onClick={() => {
                              const types = filters.propertyTypes.includes(
                                value,
                              )
                                ? filters.propertyTypes.filter(
                                    (t) => t !== value,
                                  )
                                : [...filters.propertyTypes, value];
                              onFiltersChange({
                                ...filters,
                                propertyTypes: types,
                              });
                            }}
                          >
                            {label}
                          </Badge>
                        ),
                      )}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Distressed Properties */}
              <AccordionItem value="distress">
                <AccordionTrigger className="text-sm font-medium">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Distressed Properties
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-3 pt-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="foreclosure"
                      checked={filters.foreclosure}
                      onCheckedChange={(checked) =>
                        onFiltersChange({
                          ...filters,
                          foreclosure: checked as boolean,
                        })
                      }
                    />
                    <Label
                      htmlFor="foreclosure"
                      className="text-sm cursor-pointer"
                    >
                      Foreclosure
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="preForeclosure"
                      checked={filters.preForeclosure}
                      onCheckedChange={(checked) =>
                        onFiltersChange({
                          ...filters,
                          preForeclosure: checked as boolean,
                        })
                      }
                    />
                    <Label
                      htmlFor="preForeclosure"
                      className="text-sm cursor-pointer"
                    >
                      Pre-Foreclosure
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="auction"
                      checked={filters.auction}
                      onCheckedChange={(checked) =>
                        onFiltersChange({
                          ...filters,
                          auction: checked as boolean,
                        })
                      }
                    />
                    <Label htmlFor="auction" className="text-sm cursor-pointer">
                      Auction
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="reo"
                      checked={filters.reo}
                      onCheckedChange={(checked) =>
                        onFiltersChange({ ...filters, reo: checked as boolean })
                      }
                    />
                    <Label htmlFor="reo" className="text-sm cursor-pointer">
                      REO / Bank Owned
                    </Label>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Financial Metrics */}
              <AccordionItem value="financial">
                <AccordionTrigger className="text-sm font-medium">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Financial Metrics
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-4 pt-4">
                  <div>
                    <Label className="text-xs">Min Cap Rate (%)</Label>
                    <Input
                      type="number"
                      step="0.5"
                      placeholder="e.g. 6"
                      value={filters.capRateMin || ""}
                      onChange={(e) =>
                        onFiltersChange({
                          ...filters,
                          capRateMin: e.target.value
                            ? parseFloat(e.target.value)
                            : undefined,
                        })
                      }
                    />
                  </div>
                  <div>
                    <Label className="text-xs">
                      Min Cash-on-Cash Return (%)
                    </Label>
                    <Input
                      type="number"
                      step="0.5"
                      placeholder="e.g. 10"
                      value={filters.cashOnCashMin || ""}
                      onChange={(e) =>
                        onFiltersChange({
                          ...filters,
                          cashOnCashMin: e.target.value
                            ? parseFloat(e.target.value)
                            : undefined,
                        })
                      }
                    />
                  </div>
                  <div>
                    <Label className="text-xs">
                      Max GRM (Gross Rent Multiplier)
                    </Label>
                    <Input
                      type="number"
                      step="0.5"
                      placeholder="e.g. 12"
                      value={filters.grmMax || ""}
                      onChange={(e) =>
                        onFiltersChange({
                          ...filters,
                          grmMax: e.target.value
                            ? parseFloat(e.target.value)
                            : undefined,
                        })
                      }
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Deal Indicators */}
              <AccordionItem value="deal">
                <AccordionTrigger className="text-sm font-medium">
                  <div className="flex items-center gap-2">
                    <Home className="w-4 h-4" />
                    Deal Indicators
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-3 pt-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="priceReduced"
                      checked={filters.priceReduced}
                      onCheckedChange={(checked) =>
                        onFiltersChange({
                          ...filters,
                          priceReduced: checked as boolean,
                        })
                      }
                    />
                    <Label
                      htmlFor="priceReduced"
                      className="text-sm cursor-pointer"
                    >
                      Price Reduced
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="belowMarketValue"
                      checked={filters.belowMarketValue}
                      onCheckedChange={(checked) =>
                        onFiltersChange({
                          ...filters,
                          belowMarketValue: checked as boolean,
                        })
                      }
                    />
                    <Label
                      htmlFor="belowMarketValue"
                      className="text-sm cursor-pointer"
                    >
                      Below Market Value
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="motivatedSeller"
                      checked={filters.motivatedSeller}
                      onCheckedChange={(checked) =>
                        onFiltersChange({
                          ...filters,
                          motivatedSeller: checked as boolean,
                        })
                      }
                    />
                    <Label
                      htmlFor="motivatedSeller"
                      className="text-sm cursor-pointer"
                    >
                      Motivated Seller
                    </Label>
                  </div>
                  <div className="pt-2">
                    <Label className="text-xs">Max Days on Market</Label>
                    <Input
                      type="number"
                      placeholder="e.g. 90"
                      value={filters.daysOnMarketMax || ""}
                      onChange={(e) =>
                        onFiltersChange({
                          ...filters,
                          daysOnMarketMax: e.target.value
                            ? parseInt(e.target.value)
                            : undefined,
                        })
                      }
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <div className="sticky bottom-0 pt-4 pb-2 bg-background border-t border-border mt-6">
              <Button
                className="w-full gradient-primary text-white"
                disabled={searchLoading}
                onClick={() => {
                  onSearch?.();
                  setIsOpen(false);
                }}
              >
                {searchLoading
                  ? "Searching…"
                  : `Show ${resultCount} Properties`}
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      {/* Active Filters Display */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap gap-2">
          {filters.priceMin && (
            <Badge variant="secondary" className="gap-1">
              Min ${filters.priceMin.toLocaleString()}
              <X
                className="w-3 h-3 cursor-pointer"
                onClick={() =>
                  onFiltersChange({ ...filters, priceMin: undefined })
                }
              />
            </Badge>
          )}
          {filters.priceMax && (
            <Badge variant="secondary" className="gap-1">
              Max ${filters.priceMax.toLocaleString()}
              <X
                className="w-3 h-3 cursor-pointer"
                onClick={() =>
                  onFiltersChange({ ...filters, priceMax: undefined })
                }
              />
            </Badge>
          )}
          {filters.foreclosure && (
            <Badge variant="secondary" className="gap-1">
              Foreclosure
              <X
                className="w-3 h-3 cursor-pointer"
                onClick={() =>
                  onFiltersChange({ ...filters, foreclosure: false })
                }
              />
            </Badge>
          )}
          {filters.preForeclosure && (
            <Badge variant="secondary" className="gap-1">
              Pre-Foreclosure
              <X
                className="w-3 h-3 cursor-pointer"
                onClick={() =>
                  onFiltersChange({ ...filters, preForeclosure: false })
                }
              />
            </Badge>
          )}
          {filters.capRateMin && (
            <Badge variant="secondary" className="gap-1">
              Cap Rate ≥ {filters.capRateMin}%
              <X
                className="w-3 h-3 cursor-pointer"
                onClick={() =>
                  onFiltersChange({ ...filters, capRateMin: undefined })
                }
              />
            </Badge>
          )}
          {filters.belowMarketValue && (
            <Badge variant="secondary" className="gap-1">
              Below Market
              <X
                className="w-3 h-3 cursor-pointer"
                onClick={() =>
                  onFiltersChange({ ...filters, belowMarketValue: false })
                }
              />
            </Badge>
          )}
        </div>
      )}

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">{resultCount}</span>{" "}
          properties found
        </p>
        <Select value={sortBy} onValueChange={(v) => onSortChange?.(v as SortOption)}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest</SelectItem>
            <SelectItem value="price_low">Price: Low to High</SelectItem>
            <SelectItem value="price_high">Price: High to Low</SelectItem>
            <SelectItem value="cap_rate">Highest Cap Rate</SelectItem>
            <SelectItem value="coc">Highest CoC Return</SelectItem>
            <SelectItem value="days_high">Most Days on Market</SelectItem>
            <SelectItem value="days_low">Fewest Days on Market</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
